import {
  faArrowLeft,
  faFileSignature,
  faLock,
  faTimes,
} from '@fortawesome/pro-light-svg-icons'
import { faPen } from '@fortawesome/pro-solid-svg-icons'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import moment, { Moment } from 'moment'
import React, { FunctionComponent, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Spinner } from '../../App/Components'
import Button from '../../App/Components/Button/Button'
import { useAppDispatch } from '../../App/Hooks/reduxHooks'
import { Box } from '../../Common/Box'
import { Checkbox } from '../../Common/Checkbox'
import { IUserHr } from '../../User/UsersHr/interfaces/IUserHr'
import { getHrUsers } from '../../User/UsersHr/Store/Actions'
import { Label, LabelText } from '../CreatePerson/style'

import Client from './Client/Client'
import { AvatarEdit } from './components/AvatarEdit/AvatarEdit'
import {
  AvatarText,
  BackButton,
  TopControlsWrapper,
} from './components/AvatarEdit/style'
import { EmailEditInput } from './components/EmailEditInput'
import { FirstNameEditInput } from './components/FirstNameEditInput'
import { LastNameEditInput } from './components/LastNameEditInput'
import { ResetPasswordEditInput } from './components/ResetPasswordEditInput/ResetPasswordEditInput'
import {
  Backdrop,
  BodyWrapper,
  ButtonsWrapper,
  CloseButton,
  ContractIcon,
  EditAvatar,
  EditAvatarIcon,
  EditAvatarWrapper,
  EditPersonDialogWrapper,
  HeaderWrapper,
  PasswordIcon,
  PasswordText,
  PersonHeader,
} from './style'
type Props = {
  selectedPerson: IUserHr
  setPersonCrud: (resetState: null) => void
}

export const EditPerson: FunctionComponent<Props> = ({
  selectedPerson,
  setPersonCrud,
}) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()

  const [personObject, setPersonObject] = useState<IUserHr>(selectedPerson)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [emailFeedback, setEmailFeedback] = useState<{
    error: string
    warning: string
  }>({ error: '', warning: '' })

  const [windowWidth, setWindowWidth] = useState<number>(window.innerWidth)

  const [isAvatarModalOpen, setIsAvatarModalOpen] = useState<boolean>(false)
  const [isShowPasswordFields, setIsShowPasswordFields] =
    useState<boolean>(false)

  const [newPassword, setNewPassword] = useState<string>('')
  const [confirmPassword, setConfirmPassword] = useState<string>('')

  const [updatedDate, setUpdatedDate] = useState<Moment | undefined>(
    selectedPerson.startsAt ? moment(selectedPerson.startsAt) : undefined,
  )

  useEffect(() => {
    window.addEventListener('resize', () => setWindowWidth(window.innerWidth))
    return () => {
      window.removeEventListener('resize', () =>
        setWindowWidth(window.innerWidth),
      )
    }
  }, [])

  const editPerson = () => {
    if (emailFeedback.error || emailFeedback.warning) {
      alert(t('Please fix the errors before saving the person.'))
      return
    }

    setIsLoading(true)
    Client.editPerson(
      personObject.uuid,
      personObject.firstName,
      personObject.lastName,
      personObject.email,
      personObject.hasToPunch,
      isShowPasswordFields ? newPassword : undefined,
      updatedDate,
    ).then(response => {
      if (response) {
        dispatch(getHrUsers())
        setIsLoading(false)
        setPersonCrud(null)
        setEmailFeedback({ error: '', warning: '' })
      }
    })
  }

  const handleDateChange = (newDate: any) => {
    setUpdatedDate(newDate)
  }

  return (
    <>
      <EditPersonDialogWrapper>
        <HeaderWrapper>
          <PersonHeader>{t('Edit person')}</PersonHeader>
          <CloseButton icon={faTimes} onClick={() => setPersonCrud(null)} />
        </HeaderWrapper>
        <BodyWrapper>
          {isAvatarModalOpen ? (
            <div style={{ width: '100%' }}>
              <TopControlsWrapper>
                <BackButton
                  icon={faArrowLeft}
                  onClick={() => setIsAvatarModalOpen(false)}
                />
                <AvatarText> {t('Choose an avatar')}</AvatarText>
              </TopControlsWrapper>
              <div>
                <AvatarEdit
                  selectedPersonUuid={selectedPerson.uuid}
                  setIsAvatarModalOpen={setIsAvatarModalOpen}
                  setPersonCrud={setPersonCrud}
                />
              </div>
            </div>
          ) : (
            <>
              <EditAvatarWrapper>
                <EditAvatar
                  src={`${selectedPerson.avatar}&w=144&h=144&fit=crop`}
                  windowWidth={windowWidth}
                />
                <EditAvatarIcon
                  icon={faPen}
                  onClick={() => setIsAvatarModalOpen(true)}
                />
              </EditAvatarWrapper>
              <form>
                {isLoading ? (
                  <Spinner />
                ) : (
                  <>
                    <FirstNameEditInput
                      personObject={personObject}
                      setPersonObject={setPersonObject}
                    />

                    <LastNameEditInput
                      personObject={personObject}
                      setPersonObject={setPersonObject}
                    />
                    {selectedPerson.startsAt != null && (
                      <Box display={'flex'}>
                        <ContractIcon icon={faFileSignature} />
                        <DatePicker
                          value={updatedDate ? moment(updatedDate) : null}
                          label={t('Start of contract')}
                          onChange={newDate => handleDateChange(newDate)}
                        />
                      </Box>
                    )}

                    <EmailEditInput
                      personObject={personObject}
                      setPersonObject={setPersonObject}
                      setEmailFeedback={setEmailFeedback}
                    />

                    {emailFeedback.error && (
                      <Label color={'error'}>
                        <LabelText>{emailFeedback.error}</LabelText>
                      </Label>
                    )}

                    {emailFeedback.warning && (
                      <Label color={'info'}>
                        <LabelText>{emailFeedback.warning}</LabelText>
                      </Label>
                    )}

                    <Box display={'flex'}>
                      <PasswordIcon icon={faLock} />
                      <PasswordText
                        onClick={() =>
                          setIsShowPasswordFields(!isShowPasswordFields)
                        }>
                        {t('Reset password')}
                      </PasswordText>
                    </Box>
                    {isShowPasswordFields && (
                      <ResetPasswordEditInput
                        newPassword={newPassword}
                        setNewPassword={setNewPassword}
                        confirmPassword={confirmPassword}
                        setConfirmPassword={setConfirmPassword}
                      />
                    )}
                    <Checkbox
                      mt={'16px'}
                      mb={'16px'}
                      onChange={() =>
                        setPersonObject({
                          ...personObject,
                          hasToPunch: !personObject.hasToPunch,
                        })
                      }
                      checked={personObject.hasToPunch}>
                      {t('Has to punch')}
                    </Checkbox>
                  </>
                )}

                <ButtonsWrapper windowWidth={windowWidth}>
                  <Button
                    onClick={() => setPersonCrud(null)}
                    color={'rgba(0, 0, 0, 0.3)'}>
                    {t('Cancel')}
                  </Button>
                  <Button onClick={editPerson}>{t('Save')}</Button>
                </ButtonsWrapper>
              </form>
            </>
          )}
        </BodyWrapper>
      </EditPersonDialogWrapper>
      <Backdrop onClick={() => setPersonCrud(null)} />
    </>
  )
}
